import { put, takeLatest, all, call } from 'redux-saga/effects';
import {httpGet, httpPost} from '../../Http/HttpClient';
import { displayError } from '../../Helpers/Utilities';
import {
  GET_DOWN_PAYMENT_HISTORIES,
  GetDownPaymentHistoriesAction, 
  getDownPaymentHistoriesSuccess,
  LOAD_CURATED_APPLICATION_DETAILS,
  LOAD_CURATED_VEHICLE_DETAILS,
  LOAD_CURATED_VEHICLES,
  LoadCuratedApplicationDetailsAction,
  loadCuratedApplicationDetailsSuccess,
  LoadCuratedVehicleDetailsAction,
  loadCuratedVehicleDetailsSuccess, 
  LoadCuratedVehiclesAction, 
  loadCuratedVehiclesSuccess,
} from "./actions";
import {
  PagedCuratedVehiclesAndLookupItems
} from "../../Models/PagedCuratedVehiclesAndLookupItems";
import {ApplicationDownPaymentResponse, hasValue, WebViewVehicle} from "@amsanalytics/react-shared-library";
import {CuratedSiteApplicationConfig} from "../../Models/CuratedSiteApplicationConfig";

// Vehicles
function* loadCuratedVehiclesSaga(action: LoadCuratedVehiclesAction) {
  try {
    const apiUrl = `Vehicle/getCuratedVehicles?clientId=${action.payload.clientId.toUpperCase()}`
    const response: { json: any, ok: boolean } = yield call(httpPost,
      apiUrl,
      action.payload.filters
    );

    if (response && response.ok) {
      const vehicles: PagedCuratedVehiclesAndLookupItems = yield call([response, response.json]);
      yield put(loadCuratedVehiclesSuccess(vehicles));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading configuration'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchCuratedVehicles() {
  yield takeLatest(LOAD_CURATED_VEHICLES, loadCuratedVehiclesSaga);
}

// Selected Vehicle
function* loadCuratedVehicleDetailsSaga(action: LoadCuratedVehicleDetailsAction) {
  try {
    if (!hasValue(action.payload.vehicleId) && !hasValue(action.payload.stockNumber)) {
      throw new Error('One of either VehicleId or StockNumber are required');
    }
    let apiUrl = `Vehicle/webViewVehicle?clientId=${action.payload.clientId.toUpperCase()}`
    
    if (hasValue(action.payload.vehicleId)) {
      apiUrl = apiUrl + `&vehicleId=${action.payload.vehicleId}`
    } else {
      apiUrl = apiUrl + `&stockNumber=${action.payload.stockNumber}`
    }
    
    const response: { json: any, ok: boolean } = yield call(httpGet, apiUrl);

    if (response && response.ok) {
      const vehicle: WebViewVehicle = yield call([response, response.json]);
      yield put(loadCuratedVehicleDetailsSuccess(vehicle));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading configuration'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchCuratedVehicleData() {
  yield takeLatest(LOAD_CURATED_VEHICLE_DETAILS, loadCuratedVehicleDetailsSaga);
}

// Application Details
function* loadCuratedApplicationDetailsSaga(action: LoadCuratedApplicationDetailsAction) {
  try {
    const apiUrl = `Application/getPortalLinkMetadata?customerPortalLinkId=${action.payload.customerPortalLinkId}&clientId=${action.payload.clientId.toUpperCase()}`
    const response: { json: any, ok: boolean } = yield call(httpGet, apiUrl);

    if (response && response.ok) {
      const metadata: CuratedSiteApplicationConfig = yield call([response, response.json]);
      yield put(loadCuratedApplicationDetailsSuccess(metadata));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading configuration'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchLoadCuratedApplicationDetails() {
  yield takeLatest(LOAD_CURATED_APPLICATION_DETAILS, loadCuratedApplicationDetailsSaga);
}

function* getDownPaymentHistoriesFromAmsSaga(action: GetDownPaymentHistoriesAction) {
  try {
    const response: { json: any, ok: boolean, status: number } = yield call(httpGet,
      `DownPayment/GetDownPaymentResponsesFromAms/?applicationId=${action.payload.applicationId}&clientId=${action.payload.clientId.toUpperCase()}`
    );
    if (!response.ok) {
      const error: string = yield call([response, response.json]);
      yield (put(displayError(error)));
    } else {
      const downPaymentHistories: ApplicationDownPaymentResponse[] = yield call([response, response.json]);
      yield put(getDownPaymentHistoriesSuccess(downPaymentHistories));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchGetDownPaymentHistoriesFromAmsSaga() {
  yield takeLatest(GET_DOWN_PAYMENT_HISTORIES, getDownPaymentHistoriesFromAmsSaga);
}

export default function* curatedVehicleSagas() {
  yield all([
    watchCuratedVehicles(),
    watchCuratedVehicleData(),
    watchLoadCuratedApplicationDetails(),
    watchGetDownPaymentHistoriesFromAmsSaga(),
  ]);
}
